import { useRef, useEffect } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import { Terminal as TermIcon, Minus, Maximize2, X } from 'lucide-react';
import IdeShell from '../components/ide-shell';
import CodeEditorVideo from '../components/code-editor-video';
import OutputPanel from '../components/output-panel';

/**
 * Console scene — shows console window overlay with typing output.
 * Auto-sizes height based on output lines, auto-scrolls when content overflows.
 */
export default function ConsoleScene({
  output = '',
  typingSpeed = 30,
  code = '',
  filename = 'Program.cs',
  projectName = 'HelloWorld',
}) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scrollRef = useRef(null);
  const framesPerChar = Math.max(1, Math.round((typingSpeed / 1000) * fps));
  const chars = Math.min(Math.floor(frame / framesPerChar), output.length);
  const typedText = output.slice(0, chars);

  const lineCount = code ? code.split('\n').length : 1;
  const lines = typedText.split('\n');

  // Auto-scroll to bottom as new lines appear
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chars]);

  // Dynamic height: 22px per line + 24px padding + 32px title bar, clamped 200-520
  const totalLines = output.split('\n').length;
  const bodyHeight = Math.min(Math.max(totalLines * 22 + 24, 168), 488);
  const consoleHeight = bodyHeight + 32;

  const consoleOverlay = (
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-48%,-48%)',
        width: 780, height: consoleHeight,
        background: '#0c0c0c', border: '1px solid #3a3a3a',
        display: 'flex', flexDirection: 'column',
        zIndex: 50, borderRadius: 2, overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.7), 0 2px 8px rgba(0,0,0,0.5)',
      }}>
        {/* Title bar */}
        <div style={{
          display: 'flex', alignItems: 'center', height: 32,
          background: '#1a1a1a', borderBottom: '1px solid #222',
          padding: '0 12px', gap: 8, flexShrink: 0,
        }}>
          <TermIcon size={14} color="#ccc"/>
          <span style={{ flex: 1, fontSize: 11, color: '#aaa', fontFamily: 'Consolas,monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            C:\Users\binh\source\repos\{projectName}\bin\Debug\net8.0\{projectName}.exe
          </span>
          <div style={{ display: 'flex' }}>
            <div style={{ width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Minus size={13} color="#ccc"/></div>
            <div style={{ width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Maximize2 size={11} color="#ccc"/></div>
            <div style={{ width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={13} color="#ccc"/></div>
          </div>
        </div>
        {/* Console body — scrollable */}
        <div ref={scrollRef} style={{
          flex: 1, overflow: 'auto', padding: '12px 16px',
          fontFamily: "'Cascadia Code','Consolas','Courier New',monospace",
          fontSize: 14, lineHeight: '22px',
        }}>
          {lines.map((line, i) => {
            let color = '#CCCCCC';
            if (line.includes('exited') || line.includes('process')) color = '#888';
            if (line.startsWith('Press')) color = '#888';
            return (
              <div key={i} style={{ color }}>{line || '\u00A0'}</div>
            );
          })}
        </div>
      </div>
  );

  return (
    <IdeShell projectName={projectName} filename={filename} step={4} lineCount={lineCount} overlay={consoleOverlay}>
      <CodeEditorVideo typedCode={code} step={4} lineCount={lineCount} projectName={projectName} filename={filename}/>
      <OutputPanel buildLogs={[]} />
    </IdeShell>
  );
}
